<template>
  <div class="about-page">
    <BasePreloader v-if="pending"/>
    <transition name="fade">
      <div v-if="!pending">
        <TopSection class="about-page__top-section"
                    :data="aboutData.info"/>
        <div
            class="about-page__purpose purpose">
          <div class="purpose__inner">
            <v-lazy-image v-if="aboutData.info.image2"
                          :src="aboutData.info.image2"
                          :title="createTitleMixin(aboutData.info.title2)"
                          :alt="createAltMixin(aboutData.info.title2)"
                          class="purpose__logo"
                          use-picture>
              <source v-if="aboutData.info.image2_webp"
                      :srcset="aboutData.info.image2_webp"
                      type="image/webp">
            </v-lazy-image>
            <div v-else
                 class="purpose__logo">
              <img src="../assets/img/svg/logo.svg"
                   :alt="createAltMixin(aboutData.info.title2)">
            </div>
            <div class="purpose__text text">
              <p>{{ aboutData.info.title2 }}</p>
            </div>
          </div>
        </div>
        <div
            class="about-page__we-know we-know">
          <div class="we-know__inner">
            <div class="we-know__main">
              <h3 class="we-know__title">{{ aboutData.info.title3 }}</h3>
              <div class="we-know__text text">
                <p>{{ aboutData.info.text3 }}</p>
              </div>
            </div>
            <v-lazy-image v-if="aboutData.info.image3"
                          :src="aboutData.info.image3"
                          :title="createTitleMixin(aboutData.info.title3)"
                          :alt="createAltMixin(aboutData.info.title3)"
                          class="we-know__img"
                          use-picture>
              <source v-if="aboutData.info.image3_webp"
                      :srcset="aboutData.info.image3_webp"
                      type="image/webp">
            </v-lazy-image>

            <div v-else
                 class="we-know__img">
              <img src="../assets/img/svg/we-know.svg"
                   :alt="createAltMixin(aboutData.info.title3)">
            </div>

          </div>
        </div>
        <div
            class="about-page__our-team our-team">
          <div class="our-team__image-wrap">
            <picture v-if="aboutData.info.image4" class="our-team__img">
              <source v-if="aboutData.info.image4_mob_webp"
                      :srcset="aboutData.info.image4_mob_webp"
                      type="image/webp"
                      media="(max-width: 500px)">
              <source v-if="aboutData.info.image4_mob"
                      :srcset="aboutData.info.image4_mob"
                      media="(max-width: 500px)">
              <source v-if="aboutData.info.image4_tablet_webp"
                      :srcset="aboutData.info.image4_tablet_webp"
                      type="image/webp"
                      media="(max-width: 767px)">
              <source v-if="aboutData.info.image4_tablet"
                      :srcset="aboutData.info.image4_tablet"
                      media="(max-width: 767px)">
              <source v-if="aboutData.info.image4_small_pc_webp"
                      :srcset="aboutData.info.image4_small_pc_webp"
                      type="image/webp"
                      media="(max-width: 1023px)">
              <source v-if="aboutData.info.image4_small_pc"
                      :srcset="aboutData.info.image4_small_pc"
                      media="(max-width: 1023px)">
              <source v-if="aboutData.info.image4_pc_webp"
                      :srcset="aboutData.info.image4_pc_webp"
                      type="image/webp"
                      media="(max-width: 1365px)">
              <source v-if="aboutData.info.image4_pc"
                      :srcset="aboutData.info.image4_pc"
                      media="(max-width: 1365px)">
              <source v-if="aboutData.info.image4_webp"
                      :srcset="aboutData.info.image4_webp"
                      type="image/webp">
              <img :src="aboutData.info.image4"
                   :title="createTitleMixin(aboutData.info.title4)"
                   :alt="createAltMixin(aboutData.info.title4)">
            </picture>
          </div>

          <div class="our-team__main">
            <h2 class="our-team__title">{{ aboutData.info.title4 }}</h2>
            <div class="our-team__text text">
              <p>{{ aboutData.info.text4 }}</p>
            </div>
          </div>
        </div>
        <PartnersCarousel class="about-page__partners"
                          :data="aboutData.partners"/>
        <DocumentsCarousel class="about-page__documents"
                           :data="aboutData.documents"
                           @onShowGallery="showImageGallery"
        />

        <AddressSection :title="$tAbout('Addresses')"
                        :addresses="aboutData.addresses"
                        class="about-page__address"/>

        <CoolLightBox v-if="Array.isArray(aboutData.documents)"
                      :items="aboutData.documents"
                      srcName="image"
                      :index="modalGalleryIndex"
                      @close="modalGalleryIndex = null"/>
      </div>
    </transition>


  </div>
</template>

<script>
  import {http} from '@/axios'
  import TopSection from '@/components/about/TopSection'
  import PartnersCarousel from '@/components/about/PartnersCarousel'
  import DocumentsCarousel from '@/components/about/DocumentsCarousel'
  import AddressSection from '@/components/AddressSection'
  import PhoneNumber from 'awesome-phonenumber'
  import globalMetaMixin from "@/mixins/globalMetaMixin"

  const CoolLightBox = () => import(/* webpackChunkName: "light-box" */ '@/components/CoolLightBox')

  export default {
    name: 'AboutPage',
    mixins: [globalMetaMixin],
    components: {DocumentsCarousel, PartnersCarousel, TopSection, CoolLightBox, AddressSection},
    data() {
      return {
        modalGalleryIndex: null,
        pending: false,
        aboutData: {}
      }
    },
    methods: {
      showImageGallery(index) {
        this.modalGalleryIndex = index
      },
      load() {
        this.pending = true
        return http.get('/api/about-us')
            .then(({data}) => {
              this.aboutData = data
            })
            .finally(() => {
              this.pending = false
            })
      },
      formatPhone(number) {
        const pn = new PhoneNumber(number);

        return pn.getNumber('international') || number
      },
    },
    created() {
      this.load()

      this.setMetaTitleMixin(this.$tMeta('about-meta-title'))
      this.setMetaDescriptionMixin(this.$tMeta('about-meta-description'))
      this.setOpenGraph({
        title: this.$tMeta('about-meta-title'),
        description: this.$tMeta('about-meta-description'),
      })
    }
  }
</script>

<style>

</style>
